/**
 * micro:bit 놀이터 카메라 동의(PICNIC-MB-FIELD-001 §6 3단계 · PAI-15 · LGR-09 HARD · 제어 평면 C-07 · Bearer):
 *   GET /api/courses/{c}/me/camera-consent → {state: ok | consent_required | legal_pending | reenter | unavailable}
 * Campus 의 보호자 동의를 제어 평면이 서버 간으로 물어 준다(Campus 입장 토큰의 불투명 참조). ok 가 아니면 카메라를 열지 않는다 — 가상 카드는 그대로 된다.
 * 카메라 패널이 열려 있는 동안 30초마다 다시 묻는다(Campus 화면의 동의 관문과 같다 — 철회가 곧바로). 모르면(로딩 · 오류) 닫힌 것으로 본다.
 */
import { useEffect, useMemo, useState } from "react";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";

export type CameraConsent = "ok" | "consent_required" | "legal_pending" | "reenter" | "unavailable" | "loading";
const STATES: readonly CameraConsent[] = ["ok", "consent_required", "legal_pending", "reenter", "unavailable"];
export const CONSENT_POLL_MS = 30_000;

export function useCameraConsent(enabled: boolean): CameraConsent {
  const manager = useSessionManager();
  const state = useSession();
  const courseId = state.status === "ready" ? state.session.courseId : null;
  const [consent, setConsent] = useState<CameraConsent>("loading");
  const ask = useMemo(() => (courseId ? async (): Promise<CameraConsent> => {
    try {
      const r = await manager.request<unknown>(`/api/courses/${encodeURIComponent(courseId)}/me/camera-consent`);
      const st = (r as { state?: unknown } | null)?.state;
      return STATES.includes(st as CameraConsent) ? (st as CameraConsent) : "unavailable";
    } catch {
      return "unavailable";
    }
  } : null), [manager, courseId]);
  useEffect(() => {
    if (!enabled || !ask) { setConsent("loading"); return; }
    let alive = true;
    const run = () => void ask().then((s) => alive && setConsent(s));
    run();
    const h = window.setInterval(run, CONSENT_POLL_MS);
    return () => { alive = false; window.clearInterval(h); };
  }, [enabled, ask]);
  return consent;
}
